import React, { createContext, useContext, useEffect, useState } from 'react';
import { API_URL } from './constants';
import axios from 'axios';

const UserModulesContext = createContext();

export const useUserModules = () => {
  const context = useContext(UserModulesContext);
  if (!context) {
    throw new Error('useUserModules must be used within a UserModulesProvider');
  }
  return context;
};

// Minimum modules that are always available
const getMinimumModules = (projectKey) => {
  if (projectKey === 'bat' || projectKey === 'pmi') {
    return ['settings', 'export-pricing-data', 'distribution_view'];
  }
  if (projectKey === 'asahi') {
    return ['settings', 'bat-product'];
  }
  if (projectKey === 'sweden-snus-admin') {
    return ['settings', 'support', 'sweden-snus-admin'];
  }
  return ['settings', 'support'];
};

const sanitizeModulesForProject = (modules, projectKey) => {
  const list = Array.isArray(modules) ? modules : [];
  if (projectKey === 'sweden-snus-admin') return list;
  return list.filter((id) => id !== 'sweden-snus-admin');
};

// All available modules (for reference only, not used as defaults)
const ALL_MODULES = [
  'powerbi',
  'jti-product',
  'product-detail',
  'product-multi-country',
  'product-categorisation',
  'pricing-types',
  'brand-board',
  'brand-board-summary',
  'worldmap',
  'banner-evolution',
  'product',
  'retailer',
  'distribution_brand',
  'distribution_retailer',
  'distribution_view',
  'data-sources',
  'export-pricing-data',
  'jti-export-data',
  'tocdashboard',
  'tocview',
  'toc-kpi',
  'chat',
  'support',
  'settings',
  'accessories-category',
  'sweden-snus-admin',
  'experimental-ai-price-summary',
  'experimental-ai-banner-insights',
  'experimental-chart-advanced-options',
];

export function UserModulesProvider({ children }) {
  const [enabledModules, setEnabledModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedUser, setLoadedUser] = useState(null);
  const [apiCallFailed, setApiCallFailed] = useState(false);
  const [currentProjectKey, setCurrentProjectKey] = useState(null);
  const loadingRef = React.useRef(false);
  const lastLoadedRef = React.useRef(null);

  const loadUserModules = React.useCallback(async (username, projectKey) => {
    if (!username) {
      setEnabledModules([]);
      setLoading(false);
      setLoadedUser(null);
      setCurrentProjectKey(null);
      return;
    }
    if (!projectKey) {
      setEnabledModules(getMinimumModules(projectKey));
      setLoading(false);
      setLoadedUser(username);
      setCurrentProjectKey(null);
      return;
    }

    // Prevent duplicate calls for the same user, but allow reload if explicitly requested
    const userKey = `${username}_${projectKey}`;
    // Only skip if it's the exact same call (same userKey and currently loading)
    // Allow reload if modules have changed or if it's a different load
    if (lastLoadedRef.current === userKey && loadingRef.current) {
      console.log('Skipping duplicate load for:', userKey);
      return;
    }

    console.log('Loading modules for:', username, projectKey);
    loadingRef.current = true;
    lastLoadedRef.current = userKey;
    setLoading(true);
    setCurrentProjectKey(projectKey);

    let loadedModules = [];
    let hasLocalStorage = false;

    // Always try API first to get fresh data from backend
    try {
      const response = await axios.get(`${API_URL}/user-modules/${username}`, {
        params: {
          project_key: projectKey
        },
        timeout: 5000, // 5 second timeout
        withCredentials: true
      });
      
      const modules = sanitizeModulesForProject(response.data.modules || [], projectKey);
      
      // Only use API modules if they exist (not empty array)
      // Backend should always initialize with defaults, so empty array means something is wrong
      if (modules.length > 0) {
        setEnabledModules(modules);
        setLoadedUser(username);
        setApiCallFailed(false);
        
        // Save to localStorage for future use
        localStorage.setItem(`user_modules_${username}`, JSON.stringify({ modules }));
        console.log('Loaded from API:', modules);
      } else {
        // If API returns empty, try localStorage as fallback
        try {
          const stored = localStorage.getItem(`user_modules_${username}`);
          if (stored) {
            const data = JSON.parse(stored);
            const storedModules = data.modules || [];
            if (storedModules.length > 0) {
              setEnabledModules(sanitizeModulesForProject(storedModules, projectKey));
              setLoadedUser(username);
              console.log('API returned empty, using localStorage:', storedModules);
            } else {
              // Both empty, use minimum modules
              setEnabledModules(getMinimumModules(projectKey));
              setLoadedUser(username);
              console.log('Both API and localStorage empty, using minimum modules');
            }
          } else {
            // No localStorage, use minimum modules
            setEnabledModules(getMinimumModules(projectKey));
            setLoadedUser(username);
            console.log('No localStorage, using minimum modules');
          }
        } catch (localError) {
          console.warn('Failed to load from localStorage:', localError);
          setEnabledModules(getMinimumModules(projectKey));
          setLoadedUser(username);
        }
        setApiCallFailed(true);
      }
    } catch (error) {
      console.warn('Failed to fetch user modules from API:', error.message);
      setApiCallFailed(true);
      
      // Fallback to localStorage
      try {
        const stored = localStorage.getItem(`user_modules_${username}`);
        if (stored) {
          const data = JSON.parse(stored);
          const storedModules = data.modules || [];
          if (storedModules.length > 0) {
            setEnabledModules(sanitizeModulesForProject(storedModules, projectKey));
            setLoadedUser(username);
            console.log('API failed, using localStorage:', storedModules);
          } else {
            setEnabledModules(getMinimumModules(projectKey));
            setLoadedUser(username);
            console.log('API failed and localStorage empty, using minimum modules');
          }
        } else {
          setEnabledModules(getMinimumModules(projectKey));
          setLoadedUser(username);
          console.log('API failed and no localStorage, using minimum modules');
        }
      } catch (localError) {
        console.warn('Failed to load from localStorage:', localError);
        setEnabledModules(getMinimumModules(projectKey));
        setLoadedUser(username);
      }
    }

    setLoading(false);
    loadingRef.current = false;
  }, []); // Remove all dependencies to prevent recreation

  const saveUserModules = async (username, modules, projectKey = null) => {
    if (!username) return false;
    if (!projectKey) return false;

    const sanitized = sanitizeModulesForProject(modules, projectKey);

    try {
      await axios.post(`${API_URL}/user-modules/${username}`, {
        modules: sanitized
      }, {
        params: {
          project_key: projectKey
        },
        withCredentials: true
      });
      
      // Update state immediately
      setEnabledModules(sanitized);
      setLoadedUser(username);
      
      // Also save to localStorage as backup
      localStorage.setItem(`user_modules_${username}`, JSON.stringify({ modules: sanitized }));
      
      // Force reload by clearing the last loaded ref to allow fresh API call
      lastLoadedRef.current = null;
      loadingRef.current = false;
      
      console.log('Modules saved and state updated:', sanitized);
      
      return true;
    } catch (error) {
      console.warn('Failed to save user modules to API, saving to localStorage:', error);
      
      // Fallback to localStorage
      try {
        localStorage.setItem(`user_modules_${username}`, JSON.stringify({ modules: sanitized }));
        setEnabledModules(sanitized);
        setLoadedUser(username);
        // Force reload
        lastLoadedRef.current = null;
        loadingRef.current = false;
        return true;
      } catch (localError) {
        console.error('Failed to save to localStorage:', localError);
        return false;
      }
    }
  };
  
  // Function to force reload modules from API
  const reloadModules = React.useCallback(async (username, projectKey) => {
    if (!username || !projectKey) return;
    
    // Clear cache to force fresh load
    lastLoadedRef.current = null;
    loadingRef.current = false;
    
    // Reload modules
    await loadUserModules(username, projectKey);
  }, [loadUserModules]);

  const isModuleEnabled = (moduleId) => {
    if (moduleId === 'sweden-snus-admin' && currentProjectKey !== 'sweden-snus-admin') {
      return false;
    }
    // If no modules loaded yet, return true for settings and support (always available)
    if (enabledModules.length === 0) {
      return getMinimumModules(currentProjectKey).includes(moduleId);
    }
    return enabledModules.includes(moduleId);
  };

  const getDefaultModules = (projectKey) => {
    // Always return only minimum modules - backend should provide the actual modules
    return getMinimumModules(projectKey);
  };

  const resetToDefaults = (projectKey) => {
    const defaultModules = getDefaultModules(projectKey);
    setEnabledModules(defaultModules);
    return defaultModules;
  };

  // Function to retry API call after some time (for recovery)
  const retryApiCall = React.useCallback(() => {
    setApiCallFailed(false);
    lastLoadedRef.current = null;
  }, []);

  // Initialize with minimum modules if no modules are loaded (only as last resort)
  // Don't auto-initialize - let the API call handle it
  React.useEffect(() => {
    // Only set minimum modules if we've finished loading and still have nothing
    // This prevents overriding modules that are being loaded
    if (enabledModules.length === 0 && !loading && loadedUser) {
      console.log('No modules loaded after API call, using minimum modules');
      setEnabledModules(getMinimumModules(currentProjectKey));
    }
  }, [enabledModules.length, loading, loadedUser, currentProjectKey]);

  // Temporary debug function to clear cached modules
  const clearCachedModules = (username) => {
    if (username) {
      localStorage.removeItem(`user_modules_${username}`);
      console.log(`Cleared cached modules for user: ${username}`);
      // Reset the loaded user to force reload
      setLoadedUser(null);
      lastLoadedRef.current = null;
    }
  };

  // Global debug function for browser console
  if (typeof window !== 'undefined') {
    window.debugClearUserModules = () => {
      console.log('Available user module caches:');
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('user_modules_')) {
          console.log('Found:', key);
          localStorage.removeItem(key);
        }
      });
      console.log('All user module caches cleared. Reloading page...');
      window.location.reload();
    };
  }

  const value = {
    enabledModules,
    loading,
    loadUserModules,
    saveUserModules,
    reloadModules,
    isModuleEnabled,
    getDefaultModules,
    resetToDefaults,
    setEnabledModules,
    retryApiCall,
    apiCallFailed,
    clearCachedModules
  };

  return (
    <UserModulesContext.Provider value={value}>
      {children}
    </UserModulesContext.Provider>
  );
}

export default UserModulesProvider;
